import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, mutate, downloadFile } from '../../shared/api';
import { formatMoney } from '../../shared/formatting';
import type { User } from '../../types/biApp';

type Locale = 'hr' | 'en';

const BROWN = 'var(--ad-ink)';
const GOLD = 'var(--ad-acc)';

const card: React.CSSProperties = { background: '#fff', borderRadius: 14, padding: 18, boxShadow: '0 2px 10px rgba(107,79,28,0.10)', border: '1px solid #efe6d2' };
const tabBtn = (active: boolean): React.CSSProperties => ({
    padding: '8px 16px', borderRadius: 999, border: '1px solid ' + (active ? BROWN : '#e0d6c0'),
    background: active ? BROWN : '#fff', color: active ? '#fff' : BROWN, cursor: 'pointer', fontWeight: 600, fontSize: '0.86rem',
});
const inp: React.CSSProperties = { padding: '8px 10px', borderRadius: 8, border: '1px solid #d8cdb2', fontSize: '0.86rem', width: '100%' };
const th: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: BROWN, borderBottom: '2px solid #efe6d2' };
const td: React.CSSProperties = { padding: '8px 10px', fontSize: '0.84rem', borderBottom: '1px solid #f3ecdb', whiteSpace: 'nowrap' };
const tdNum: React.CSSProperties = { ...td, textAlign: 'right' };

type Rates = {
    mio_i_pct: number; mio_ii_pct: number; health_employer_pct: number;
    tax_low_pct: number; tax_high_pct: number; tax_threshold_monthly_eur: number; personal_deduction: number;
    osnovica: number;
};
type ConfigResp = {
    config: { company_id: number | null; fiscal_year: number; rates: Rates; personal_deduction: number; tax_base: string; exists: boolean };
    defaults: Rates;
    disclaimer: string;
};
type RunRow = {
    id: number; company_id: number; company_name: string | null; fiscal_year: number; fiscal_month: number;
    calculation_number: number; status: string; locked_at: string | null; locked_by_name: string | null; payslip_count: number;
};
type Payslip = {
    id: number; employee_id: number; first_name: string | null; last_name: string | null; oib: string | null;
    bruto1: number; employee_mio_i: number; employee_mio_ii: number; income_base: number;
    personal_deduction: number; taxable: number; income_tax: number; neto: number;
    employer_health: number; employer_total_cost: number; net_payout: number;
};
type Reconcile = {
    ours: Record<string, number>;
    summary: Record<string, number> | null;
    diff: Record<string, number | null> | null;
    has_summary: boolean;
};
type Company = { id: number; code: string; name: string };

const MONTHS_HR = ['', 'Siječanj', 'Veljača', 'Ožujak', 'Travanj', 'Svibanj', 'Lipanj', 'Srpanj', 'Kolovoz', 'Rujan', 'Listopad', 'Studeni', 'Prosinac'];

export function PayrollTab({ token, locale }: { token: string; locale: Locale; user: User }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const eur = (n: number) => formatMoney(Number(n) || 0, locale);

    const now = new Date();
    const [companies, setCompanies] = useState<Company[] | null>(null);
    const [companyId, setCompanyId] = useState<string>('');
    const [year, setYear] = useState<number>(now.getFullYear());
    const [month, setMonth] = useState<number>(now.getMonth() + 1);

    const [cfg, setCfg] = useState<ConfigResp | null>(null);
    const [cfgBusy, setCfgBusy] = useState(false);
    const [cfgMsg, setCfgMsg] = useState<string | null>(null);
    const [editRates, setEditRates] = useState<Rates | null>(null);

    const [runs, setRuns] = useState<RunRow[] | null>(null);
    const [selectedRun, setSelectedRun] = useState<RunRow | null>(null);
    const [payslips, setPayslips] = useState<Payslip[] | null>(null);
    const [recon, setRecon] = useState<Reconcile | null>(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    // Lista firmi iz /hr/employees/ref-data (dostupno HR managerima; candidates je superadmin-only).
    useEffect(() => {
        api<{ companies: Company[] }>('/hr/employees/ref-data', token)
            .then((r) => setCompanies(r.companies ?? []))
            .catch(() => setCompanies([]));
    }, [token]);

    const loadConfig = useCallback(() => {
        setCfg(null); setEditRates(null);
        const cq = companyId ? `&company_id=${companyId}` : '';
        api<ConfigResp>(`/hr/payroll/config?fiscal_year=${year}${cq}`, token)
            .then((r) => { setCfg(r); setEditRates(r.config.rates); })
            .catch((e) => setErr(String(e?.message ?? e)));
    }, [token, companyId, year]);

    const loadRuns = useCallback(() => {
        setRuns(null);
        const cq = companyId ? `?company_id=${companyId}` : '';
        api<{ runs: RunRow[] }>(`/hr/payroll/runs${cq}`, token)
            .then((r) => setRuns(r.runs))
            .catch((e) => { setErr(String(e?.message ?? e)); setRuns([]); });
    }, [token, companyId]);

    useEffect(loadConfig, [loadConfig]);
    useEffect(loadRuns, [loadRuns]);

    const saveConfig = () => {
        if (!editRates) return;
        setCfgBusy(true); setCfgMsg(null);
        mutate('/hr/payroll/config', token, 'PUT', {
            company_id: companyId ? Number(companyId) : null,
            fiscal_year: year,
            rates: editRates,
            personal_deduction: editRates.personal_deduction,
            tax_base: cfg?.config.tax_base ?? 'bruto1',
        }).then(() => { setCfgMsg(t('Spremljeno.', 'Saved.')); loadConfig(); })
            .catch((e) => setCfgMsg(String(e?.message ?? e)))
            .finally(() => setCfgBusy(false));
    };

    const createRun = () => {
        if (!companyId) { setErr(t('Odaberi firmu.', 'Select a company.')); return; }
        setBusy(true); setErr(null);
        mutate<{ id: number }>('/hr/payroll/runs', token, 'POST', {
            company_id: Number(companyId), fiscal_year: year, fiscal_month: month,
        }).then(() => loadRuns()).catch((e) => setErr(String(e?.message ?? e))).finally(() => setBusy(false));
    };

    const openRun = (run: RunRow) => {
        setSelectedRun(run); setPayslips(null); setRecon(null);
        api<{ payslips: Payslip[] }>(`/hr/payroll/runs/${run.id}/payslips`, token)
            .then((r) => setPayslips(r.payslips)).catch((e) => { setErr(String(e?.message ?? e)); setPayslips([]); });
        api<Reconcile>(`/hr/payroll/runs/${run.id}/reconcile`, token).then(setRecon).catch(() => setRecon(null));
    };

    const generate = (run: RunRow) => {
        setBusy(true); setErr(null);
        mutate<{ generated: number }>(`/hr/payroll/runs/${run.id}/generate`, token, 'POST', {})
            .then((r) => { loadRuns(); openRun(run); if (r.generated === 0) setErr(t('Nijedan djelatnik nije imao osnovu za izračun (postavi koeficijent/osnovicu).', 'No employee had a calculation base (set coefficient/base).')); })
            .catch((e) => setErr(String(e?.message ?? e))).finally(() => setBusy(false));
    };

    const downloadJoppd = (run: RunRow) =>
        downloadFile(`/hr/payroll/runs/${run.id}/joppd`, token, `JOPPD_${run.fiscal_year}_${String(run.fiscal_month).padStart(2, '0')}.xml`).catch((e) => setErr(String(e?.message ?? e)));
    const downloadPayslip = (p: Payslip) =>
        downloadFile(`/hr/payslips/${p.id}/pdf`, token, `Platna_lista_${p.last_name ?? ''}.pdf`).catch((e) => setErr(String(e?.message ?? e)));

    const years = useMemo(() => { const y = now.getFullYear(); return [y + 1, y, y - 1, y - 2]; }, []);
    const setRate = (k: keyof Rates, v: string) => setEditRates((p) => (p ? { ...p, [k]: Number(v) } : p));

    return (
        <div>
            {/* Banner */}
            <div style={{ background: '#fdf3e0', border: '1px solid ' + GOLD, borderRadius: 10, padding: '10px 14px', marginBottom: 14, color: 'var(--ad-acc-3)', fontSize: '0.84rem', fontWeight: 600 }}>
                ⚠ {t('Stope su standardne 2026 — POTVRDITI s knjigovođom.', 'Rates are standard 2026 — CONFIRM with the accountant.')}
            </div>

            {/* Period selector */}
            <div style={{ ...card, marginBottom: 14, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
                <label style={{ flex: '2 1 200px' }}>{t('Firma', 'Company')}
                    <select style={inp} value={companyId} onChange={(e) => { setCompanyId(e.target.value); setSelectedRun(null); }}>
                        <option value="">{t('— odaberi —', '— select —')}</option>
                        {(companies ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </label>
                <label style={{ flex: '1 1 110px' }}>{t('Godina', 'Year')}
                    <select style={inp} value={year} onChange={(e) => setYear(Number(e.target.value))}>
                        {years.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                </label>
                <label style={{ flex: '1 1 140px' }}>{t('Mjesec', 'Month')}
                    <select style={inp} value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                        {MONTHS_HR.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                    </select>
                </label>
            </div>

            {err ? <p className="error" style={{ fontSize: '0.84rem' }}>{err}</p> : null}

            {/* Config editor */}
            <div style={{ ...card, marginBottom: 14 }}>
                <strong style={{ color: BROWN }}>{t('Stope i osnovica', 'Rates & base')} {cfg?.config.exists ? '' : <span className="muted" style={{ fontWeight: 400, fontSize: '0.78rem' }}>({t('zadane vrijednosti', 'defaults')})</span>}</strong>
                {!editRates ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : (
                    <>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, margin: '12px 0' }}>
                            {([
                                ['mio_i_pct', t('MIO I. (%)', 'Pension I (%)')],
                                ['mio_ii_pct', t('MIO II. (%)', 'Pension II (%)')],
                                ['health_employer_pct', t('Zdravstvo posl. (%)', 'Employer health (%)')],
                                ['tax_low_pct', t('Porez niža (%)', 'Tax low (%)')],
                                ['tax_high_pct', t('Porez viša (%)', 'Tax high (%)')],
                                ['tax_threshold_monthly_eur', t('Prag mj. (€)', 'Threshold (€)')],
                                ['personal_deduction', t('Osobni odbitak (€)', 'Personal deduction (€)')],
                                ['osnovica', t('Osnovica (€)', 'Base salary (€)')],
                            ] as [keyof Rates, string][]).map(([k, label]) => (
                                <label key={k} style={{ fontSize: '0.78rem', color: BROWN }}>{label}
                                    <input style={inp} type="number" step="0.01" value={editRates[k] ?? 0} onChange={(e) => setRate(k, e.target.value)} />
                                </label>
                            ))}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <button type="button" style={tabBtn(true)} disabled={cfgBusy} onClick={saveConfig}>{cfgBusy ? '…' : t('Spremi stope', 'Save rates')}</button>
                            {cfgMsg ? <span className="muted" style={{ fontSize: '0.82rem' }}>{cfgMsg}</span> : null}
                        </div>
                    </>
                )}
            </div>

            {/* Runs */}
            <div style={{ ...card, marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <strong style={{ color: BROWN }}>{t('Obračuni', 'Payroll runs')}</strong>
                    <button type="button" style={tabBtn(true)} disabled={busy || !companyId} onClick={createRun}>＋ {t('Novi obračun', 'New run')} ({MONTHS_HR[month]} {year})</button>
                </div>
                {runs === null ? <p className="muted">{t('Učitavam…', 'Loading…')}</p>
                    : runs.length === 0 ? <p className="muted" style={{ fontStyle: 'italic' }}>{t('Nema obračuna. Kreiraj prvi.', 'No runs yet. Create the first one.')}</p>
                        : (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead><tr>
                                        <th style={th}>{t('Razdoblje', 'Period')}</th>
                                        <th style={th}>{t('Firma', 'Company')}</th>
                                        <th style={th}>{t('Status', 'Status')}</th>
                                        <th style={th}>{t('Listi', 'Slips')}</th>
                                        <th style={th}></th>
                                    </tr></thead>
                                    <tbody>
                                        {runs.map((r) => (
                                            <tr key={r.id} style={{ background: selectedRun?.id === r.id ? '#fdf8ec' : undefined }}>
                                                <td style={td}>{MONTHS_HR[r.fiscal_month]} {r.fiscal_year}{r.calculation_number > 1 ? ` · #${r.calculation_number}` : ''}</td>
                                                <td style={td}>{r.company_name ?? '—'}</td>
                                                <td style={td}><span style={{ padding: '2px 8px', borderRadius: 999, fontSize: '0.72rem', background: r.status === 'locked' ? '#f3ece0' : r.status === 'generated' ? '#e4f3e9' : '#eef0f6', color: BROWN }}>{r.status}</span></td>
                                                <td style={td}>{r.payslip_count}</td>
                                                <td style={td}>
                                                    <div style={{ display: 'flex', gap: 6 }}>
                                                        <button type="button" style={{ ...tabBtn(false), padding: '4px 10px' }} onClick={() => openRun(r)}>{t('Otvori', 'Open')}</button>
                                                        <button type="button" style={{ ...tabBtn(false), padding: '4px 10px' }} disabled={busy || r.status === 'locked'} onClick={() => generate(r)}>{t('Izračunaj', 'Generate')}</button>
                                                        <button type="button" style={{ ...tabBtn(false), padding: '4px 10px' }} onClick={() => downloadJoppd(r)}>JOPPD XML</button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
            </div>

            {/* Payslips + reconcile */}
            {selectedRun ? (
                <div style={card}>
                    <strong style={{ color: BROWN }}>{t('Platne liste', 'Payslips')} — {MONTHS_HR[selectedRun.fiscal_month]} {selectedRun.fiscal_year}</strong>

                    {recon ? (
                        <div style={{ margin: '10px 0', padding: '10px 12px', borderRadius: 8, background: '#faf6ec', fontSize: '0.82rem' }}>
                            <strong style={{ color: BROWN }}>{t('Usklađenje vs evidencija plaća', 'Reconcile vs payroll summary')}: </strong>
                            {!recon.has_summary ? <span className="muted">{t('Nema bi_payroll_summaries zapisa za usporedbu.', 'No payroll summary record to compare.')}</span>
                                : recon.diff ? (
                                    <span>
                                        {t('Bruto razlika', 'Gross diff')}: <strong style={{ color: Math.abs(recon.diff.gross ?? 0) < 1 ? '#1f7a36' : '#b14040' }}>{eur(recon.diff.gross ?? 0)}</strong>
                                        {'  ·  '}{t('Neto razlika', 'Net diff')}: <strong style={{ color: Math.abs(recon.diff.net ?? 0) < 1 ? '#1f7a36' : '#b14040' }}>{eur(recon.diff.net ?? 0)}</strong>
                                    </span>
                                ) : null}
                        </div>
                    ) : null}

                    {payslips === null ? <p className="muted">{t('Učitavam…', 'Loading…')}</p>
                        : payslips.length === 0 ? <p className="muted" style={{ fontStyle: 'italic' }}>{t('Nema platnih listi. Klikni „Izračunaj”.', 'No payslips. Click “Generate”.')}</p>
                            : (
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead><tr>
                                            <th style={th}>{t('Djelatnik', 'Employee')}</th>
                                            <th style={{ ...th, textAlign: 'right' }}>Bruto 1</th>
                                            <th style={{ ...th, textAlign: 'right' }}>MIO I+II</th>
                                            <th style={{ ...th, textAlign: 'right' }}>{t('Porez', 'Tax')}</th>
                                            <th style={{ ...th, textAlign: 'right' }}>Neto</th>
                                            <th style={{ ...th, textAlign: 'right' }}>{t('Za isplatu', 'Payout')}</th>
                                            <th style={{ ...th, textAlign: 'right' }}>{t('Trošak', 'Cost')}</th>
                                            <th style={th}></th>
                                        </tr></thead>
                                        <tbody>
                                            {payslips.map((p) => (
                                                <tr key={p.id}>
                                                    <td style={td}>{(p.last_name ?? '') + ' ' + (p.first_name ?? '')}</td>
                                                    <td style={tdNum}>{eur(p.bruto1)}</td>
                                                    <td style={tdNum}>{eur(p.employee_mio_i + p.employee_mio_ii)}</td>
                                                    <td style={tdNum}>{eur(p.income_tax)}</td>
                                                    <td style={{ ...tdNum, fontWeight: 700 }}>{eur(p.neto)}</td>
                                                    <td style={tdNum}>{eur(p.net_payout)}</td>
                                                    <td style={tdNum}>{eur(p.employer_total_cost)}</td>
                                                    <td style={td}><button type="button" style={{ ...tabBtn(false), padding: '4px 10px' }} onClick={() => downloadPayslip(p)}>PDF</button></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                </div>
            ) : null}
        </div>
    );
}
